'use client';
import { HamburgerMenuIcon } from '@radix-ui/react-icons';
import finalLogo from '../assets/images/final-logo.svg';
import { useState, useEffect } from 'react';
import { SidebarItem } from './shared/SidebarItem';
import { mainNavItems, otherNavItems, subAdminNavItems } from '../config/sidebarItems';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../hooks/useAuth';
import React from 'react'; // Added missing import for React
import { ChevronDown, ChevronUp } from 'lucide-react'; // Import ChevronDown and ChevronUp
import { Toast } from './shared/Toast';
import { AdminService } from '../services';
import EditProfileModal from './modals/editProfileModal';
import ChangePasswordModal from './modals/changePasswordModal';
import Modal from './modals/modal';
import passwordChangeSuccessIcon from '../assets/icons/passwordSetSuccess.svg';
import profileUpdatedModalIcon from '../assets/icons/profile-updated-modal-icon.svg';

export default function Navbar({ name, profileUrl }) {
	const [menuOpen, setMenuOpen] = useState(false);
	const [activeItem, setActiveItem] = useState('dashboard');
	const [isSettingsOpen, setIsSettingsOpen] = useState(false); // New state for Settings dropdown
	const navigate = useNavigate();
	const { getUserDisplayName, getUserPicture, updateUserData, logout, user } = useAuthContext();
	const [isUpdateProfileModalOpen, setIsUpdateProfileModalOpen] = useState(false); // New state for Update Profile modal
	const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false); // New state for Change Password modal
	const [passwordChangeSuccessMessage, setPasswordChangeSuccessMessage] = useState(''); // New state for password change success message
	const [isProfileUpdatedModalOpen, setIsProfileUpdatedModalOpen] = useState(false);
	const [userProfile, setUserProfile] = useState({
		firstName: user?.firstName || '',
		lastName: user?.lastName || '',
		email: user?.email || '',
		photo: getUserPicture()
	});

	useEffect(() => {
		if (user) {
			setUserProfile({
				firstName: user.firstName || '',
				lastName: user.lastName || '',
				email: user.email || '',
				photo: user.pictureUrl || getUserPicture()
			});
		}
	}, [user, getUserPicture]);

	const userName = getUserDisplayName();
	const userPicture = getUserPicture();

	const handleChangePassword = (message) => {
		setIsChangePasswordModalOpen(false); // Close the change password modal
		setPasswordChangeSuccessMessage(message); // Set the custom success message
		setIsProfileUpdatedModalOpen(true); // Open the profile updated modal, NOT update profile modal
	};

	const closeProfileUpdatedOrPasswordChangedModal = () => {
		setIsProfileUpdatedModalOpen(false);
		if (passwordChangeSuccessMessage) {
			logout();
			navigate('/admin/login', { replace: true });
		}
	};

	const handleSaveProfile = async (updatedProfile) => {
		try {
			// Prepare profile data for API - only include fields that have values
			const profileData = {};

			// Only add fields that have values and are different from current user data
			if (updatedProfile.firstName && updatedProfile.firstName.trim() !== '') {
				profileData.firstName = updatedProfile.firstName.trim();
			}
			if (updatedProfile.lastName && updatedProfile.lastName.trim() !== '') {
				profileData.lastName = updatedProfile.lastName.trim();
			}
			if (updatedProfile.profileImage) {
				profileData.profileImage = updatedProfile.profileImage;
			}

			// Check if at least one field is provided
			if (Object.keys(profileData).length === 0) {
				Toast.error('Please provide at least one field to update');
				return;
			}

			// Call the update profile API
			const response = await AdminService.updateProfile(user.userId, profileData);

			if (response.status === 200 && response.user) {
				// Update auth context with new customer data
				updateUserData(response.user);

				// Clear any previous password change messages
				setPasswordChangeSuccessMessage('');

				// Close modal and show confirmation
				setIsUpdateProfileModalOpen(false); // Close the edit profile modal
				setIsProfileUpdatedModalOpen(true); // Open the profile updated modal
			} else {
				// Handle error
				const errorMessage = response.message || 'Failed to update profile';
				Toast.error(errorMessage);
			}
		} catch (error) {
			Toast.error('Network error. Please try again.');
		}
	};

	// Filter navigation items based on accessibleRoutes
	const filterAccessibleNavItems = (navItems) => {
		const accessibleRoutes = user?.accessibleRoutes || [];
		return navItems.filter((item) => accessibleRoutes.includes(item.path));
	};

	// Combine all possible navigation items and then filter
	const allPossibleNavItems = [...mainNavItems, ...subAdminNavItems];
	const navigationItems = filterAccessibleNavItems(allPossibleNavItems);

	const handleItemClick = (item) => {
		setActiveItem(item.id);
		navigate(item.path);
	};

	const handleLogout = () => {
		logout();
		navigate('/admin/login', { replace: true });
	};

	return (
		<header className="w-full">
			{/* Top strip */}
			{/* <div className="bg-dark-blue text-white text-[12px] sm:text-[14px] text-center py-1 sm:py-2 px-2 sm:px-4">
				WriteForm is not affiliated with or endorsed by the American Psychological Association.
				References to APA style are for educational and formatting guidance only.
			</div> */}

			{/* Main nav */}
			<div className="flex items-center justify-between px-4 sm:px-8 py-3 sm:py-4 bg-white shadow-sm">
				<div
					className="flex items-center cursor-pointer"
					onClick={() => {
						const dashboardRoute = user?.accessibleRoutes?.find((route) =>
							route.toLowerCase().includes('dashboard')
						);
						if (dashboardRoute) {
							navigate(dashboardRoute);
						} else {
							console.warn('No route found for this user.');
						}
					}}
				>
					<img src={finalLogo} alt="WriteForm" className="h-7 sm:h-8 w-auto" />
				</div>

				<div className="hidden lg:flex items-center space-x-4 lg:space-x-6">
					{/* <button className="flex items-center space-x-2 border rounded-md px-2 lg:px-3 py-1 text-xs sm:text-sm font-medium text-dark-blue hover:bg-gray-50 transition-colors">
					<img src={questionIcon} alt="Quick Tour" className="w-4 h-4 lg:w-[22px] lg:h-[22px]" />
					<span>Quick Tour</span>
				</button> */}

					<div className="flex items-center text-dark-blue font-medium text-xs sm:text-sm lg:text-base">
						<img
							src={userPicture}
							alt="User"
							className="w-5 h-5 lg:w-[32px] lg:h-[32px] rounded-full mr-2"
						/>
						<span className="hidden lg:flex flex-col leading-tight">
							{/* <span className="font-[400] text-[12px]">Welcome</span> */}
							<span className="font-[500] text-[16px]">{userName}</span>
						</span>
						<span className="flex lg:hidden font-[400] text-[12px]">
							Welcome <span className="font-[500] text-[16px] ml-1">{userName}</span>
						</span>
					</div>
				</div>

				<button
					className="lg:hidden text-dark-blue hover:text-[#4b2fbf] transition-colors"
					onClick={() => setMenuOpen(!menuOpen)}
				>
					<HamburgerMenuIcon className="w-6 h-6" />
				</button>
			</div>

			{/* Mobile dropdown */}
			{menuOpen && (
				<div className="lg:hidden bg-white shadow-sm px-4 py-3 space-y-3">
					{/* <button className="flex items-center space-x-2 text-dark-blue w-full">
					<img src={questionIcon} alt="Quick Tour" className="w-5 h-5" />
					<span>Quick Tour</span>
				</button> */}

					{/* <div className="border-t border-gray-200 my-2"></div> */}

					<div className="flex items-center space-x-2 text-dark-blue">
						<img src={userPicture} alt="User" className="w-5 h-5 rounded-full" />
						<span className="font-[400]">
							Welcome <span className="font-[500]">{getUserDisplayName()}</span>
						</span>
					</div>

					{/* Divider for sidebar items */}
					<div className="border-t border-gray-200 my-2"></div>

					<div className="space-y-2">
						<div className="space-y-2">
							{navigationItems.map((item) => (
								<SidebarItem
									key={item.id}
									icon={item.icon}
									label={item.label}
									active={activeItem === item.id}
									variant={item.variant}
									onClick={() => handleItemClick(item)}
								/>
							))}
						</div>

						<div className="space-y-2.5">
							{otherNavItems.map((item) => (
								<React.Fragment key={item.id}>
									<SidebarItem
										key={item.id}
										icon={item.icon}
										label={item.label}
										active={activeItem === item.id}
										variant={item.variant}
										extraContent={
											item.id === 'settings' &&
											(isSettingsOpen ? (
												<ChevronUp className="w-4 h-4 text-dark-blue" />
											) : (
												<ChevronDown className="w-4 h-4 text-dark-blue" />
											))
										}
										onClick={() => {
											if (item.id === 'logout') {
												handleLogout();
											} else if (item.id === 'settings') {
												setIsSettingsOpen(!isSettingsOpen);
											} else {
												handleItemClick(item);
											}
										}}
									/>
									{item.children && isSettingsOpen && (
										<div className="ml-8 space-y-2.5">
											{item.children.map((child) => (
												<SidebarItem
													key={child.id}
													icon={child.icon}
													label={child.label}
													active={activeItem === child.id}
													onClick={() => {
														if (child.id === 'profile') {
															setIsUpdateProfileModalOpen(true);
														} else if (child.id === 'security') {
															setIsChangePasswordModalOpen(true);
														} else {
															handleItemClick(child);
														}
													}}
												/>
											))}
										</div>
									)}
								</React.Fragment>
							))}
						</div>
					</div>
				</div>
			)}
			<EditProfileModal
				isOpen={isUpdateProfileModalOpen}
				onClose={() => setIsUpdateProfileModalOpen(false)}
				userProfile={userProfile}
				onSaveProfile={handleSaveProfile}
			/>
			<ChangePasswordModal
				isOpen={isChangePasswordModalOpen}
				onClose={() => setIsChangePasswordModalOpen(false)}
				onChangePassword={handleChangePassword}
			/>

			<Modal
				isOpen={isProfileUpdatedModalOpen}
				onClose={() => {
					setIsProfileUpdatedModalOpen(false);
					setPasswordChangeSuccessMessage(''); // Clear message on close
				}}
				icon={
					<img
						src={passwordChangeSuccessMessage ? passwordChangeSuccessIcon : profileUpdatedModalIcon}
						alt={passwordChangeSuccessMessage ? 'Password Changed' : 'Profile Updated'}
						className="mx-auto"
					/>
				}
				heading={
					passwordChangeSuccessMessage ? 'Password changed successfully' : 'Profile setting updated'
				}
				description={passwordChangeSuccessMessage || 'Your account profile has been updated.'}
				buttonText="Close"
				onButtonClick={() => closeProfileUpdatedOrPasswordChangedModal()}
			/>
		</header>
	);
}
